import { isToss } from '../../lib/platform';
import { loadTossSdk, TOSS_EVENTS, tossLog } from '../../lib/toss';
import { clientConfig } from '../../lib/config';

/**
 * 리워드 광고 재생. 반환값은 **보상을 지급해도 되는가**.
 *
 * 표면마다 지급 기준이 다르다 — 정책이 다르기 때문이다.
 *
 * **웹(애드센스): 광고 쪽에서 무슨 일이 나든 "봤다"로 친다.**
 * 광고가 안 뜨거나, 중간에 오류가 나거나, 사업자 서버가 죽어도 보상을 지급한다.
 * 사용자는 이미 기다렸는데 우리 사정도 아닌 이유로 횟수를 못 받으면 서비스를 떠난다.
 * 잃는 건 해석 1회 원가(₩2 남짓)이고, 실제 비용 방어선은 광고가 아니라
 * 전역 일일 상한(`LLM_DAILY_CAP`)이다 — doc/usage-limit.md 참고.
 *
 * **토스 미니앱: 광고를 실제로 보여준 경우에만 `userEarnedReward`를 따진다.**
 * 앱인토스는 "광고 클릭과 동시에 보상 제공"을 금지한다. 그래서 광고가 떴는데 사용자가
 * 중간에 닫았으면 지급하지 않는다.
 *
 * 다만 **광고를 띄우지 못한 경우는 다르다.** 광고 그룹 ID가 아직 없거나(정산 승인 전),
 * SDK가 없거나, 불러오기가 실패했다면 광고 거래 자체가 없었던 것이라 금지 조항과 무관하다.
 * 그때는 웹과 같은 관대한 정책으로 지급한다 — 안 그러면 사용자는 **광고를 볼 방법도 없이**
 * "광고를 끝까지 보셔야 해요"만 반복해서 보게 된다. 비용 방어선은 여기가 아니라
 * 하루 상한(`DAILY_HARD_LIMIT`)과 전역 상한(`LLM_DAILY_CAP`)이다.
 */

/** 광고를 **불러오는** 데 이만큼 걸리면 포기한다 */
const AD_LOAD_TIMEOUT_MS = 20_000;

/**
 * 광고가 **떠 있는 동안**의 안전장치.
 *
 * ⚠️ 여기에 짧은 값을 두면 안 된다. 리워드 광고는 15~30초짜리라 20초 타임아웃을 걸었더니,
 * 광고가 아직 화면에 떠 있는데 우리 쪽이 실패로 판단해 보상을 주고 **뒤에서 다음 화면을
 * 시작**했다. 사용자가 광고를 닫으면 이미 딴 화면이라 먹통처럼 보였다 (2026-08-28 실기기 제보).
 * 광고는 사용자가 닫을 때까지 기다리는 게 맞고, 이 값은 SDK가 아무 이벤트도 주지 않을 때의 탈출구다.
 */
const AD_SHOW_TIMEOUT_MS = 5 * 60_000;

/**
 * 지금 이 표면에서 **광고를 실제로 띄울 수 있는가.**
 *
 * 화면 문구를 사실에 맞추기 위한 값이다. 웹은 아직 광고 SDK가 붙지 않아 "광고 보고 이어서
 * 보기"라고 해놓고 아무것도 안 띄운 채 통과시켰다 — 사용자에게 손해는 아니지만 거짓말이었다.
 */
export async function canShowAd(): Promise<boolean> {
  if (!isToss) return false; // 웹: AdSense Offerwall 미연결 (아래 showAd의 TODO)
  const [sdk, { tossAdGroupId }] = await Promise.all([loadTossSdk(), clientConfig()]);
  return !!sdk && !!tossAdGroupId;
}

export async function playRewardedAd(): Promise<boolean> {
  if (isToss) return playTossRewardedAd();
  try {
    await withTimeout(showAd(), AD_LOAD_TIMEOUT_MS);
  } catch {
    // 광고 오류·시간 초과 — 위 정책대로 시청한 것으로 본다
  }
  return true;
}

/** 실제 광고 노출(웹). AdSense Offerwall / 리워드 광고 SDK를 붙일 자리 */
async function showAd(): Promise<void> {
  // TODO: 광고 SDK 연결 (승인 후). 연결 전까지는 즉시 완료로 둔다.
}

/**
 * 토스 인앱 리워드 광고.
 * 로드 → 표시 → `userEarnedReward` 를 기다린다. 그 이벤트 없이 닫히면 `false`.
 */
async function playTossRewardedAd(): Promise<boolean> {
  const sdk = await loadTossSdk();
  // 광고 그룹 ID는 **서버가 내려준다** — 번들에 박으면 광고를 켜는 데 재심사가 필요하다 (lib/config.ts)
  const { tossAdGroupId } = await clientConfig();
  // 광고를 틀 수단이 없다 — 시청을 요구할 수 없으니 지급한다 (위 정책)
  if (!sdk || !tossAdGroupId) return true;
  const options = { adGroupId: tossAdGroupId };

  try {
    await withTimeout(
      new Promise<void>((resolve, reject) => {
        sdk.loadFullScreenAd({
          options,
          onEvent: (e) => {
            if (e.type === 'loaded') resolve();
          },
          onError: reject,
        });
      }),
      AD_LOAD_TIMEOUT_MS,
    );
  } catch {
    return true; // 불러오기 실패 — 광고가 없었던 것이므로 지급한다
  }

  try {
    return await withTimeout(
      new Promise<boolean>((resolve, reject) => {
        /**
         * 보상 이벤트(`userEarnedReward`)는 광고가 **끝나는 순간** 오고, 닫기(X)는 그 뒤에
         * 사용자가 누른다. 그래서 보상 이벤트에서 곧바로 진행하면 **광고가 떠 있는 채로**
         * 뒤 화면이 바뀌고, 사용자가 광고를 닫는 순간 딴 화면을 만난다.
         * 받은 사실만 기억해 두고, 실제 진행은 `dismissed`(닫힘) 이후에 한다.
         */
        let earned = false;
        sdk.showFullScreenAd({
          options,
          onEvent: (e) => {
            if (e.type === 'userEarnedReward') {
              earned = true;
              tossLog(TOSS_EVENTS.adComplete);
            } else if (e.type === 'dismissed') resolve(earned);
            // 광고가 뜨지 못했다 — 사용자 잘못이 아니므로 지급한다
            else if (e.type === 'failedToShow') resolve(true);
          },
          onError: reject,
        });
      }),
      AD_SHOW_TIMEOUT_MS,
    );
  } catch {
    return true; // 표시 중 오류·시간 초과 — 사용자는 기다렸다
  }
}

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    p,
    new Promise<T>((_, reject) => setTimeout(() => reject(new Error('ad timeout')), ms)),
  ]);
}
